import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useNavigate } from 'react-router-dom';
import { lumi } from '../lib/lumi';
import {Users, CheckCircle, Clock, Package, FileText, DollarSign, Eye, Filter} from 'lucide-react';
import { toast } from 'react-hot-toast';

interface ServiceRequest {
  _id: string;
  userId: string;
  contactName: string;
  contactEmail: string;
  phone: string;
  serviceTypes: string[];
  status: 'draft' | 'submitted' | 'in-progress' | 'completed';
  address: string;
  city: string;
  country: string;
  createdAt: string;
  invoiceId?: string;
}

interface Invoice {
  _id: string;
  requestId: string;
  userId: string;
  invoiceNumber: string;
  serviceTypes: string[];
  totalAmount: number;
  status: 'pending' | 'approved' | 'rejected' | 'cancelled';
  paymentStatus: 'unpaid' | 'paid' | 'failed';
  paymentMethod?: string;
  paymentReference?: string;
  paidAt?: string;
  createdAt: string;
  updatedAt: string;
}

const AdminDashboard = () => {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [requests, setRequests] = useState<ServiceRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [generatingInvoice, setGeneratingInvoice] = useState<string | null>(null);

  // Redirect if not admin
  useEffect(() => {
    if (user && user.userRole !== 'ADMIN') {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  useEffect(() => {
    const fetchAllRequests = async () => {
      try {
        // @ts-ignore
        if (lumi.entities?.ServiceRequests) {
          // @ts-ignore
          const { list } = await lumi.entities.ServiceRequests.list({
            filter: selectedStatus === 'all' ? {} : { status: selectedStatus },
            sort: { createdAt: -1 },
            limit: 100
          });
          setRequests(list || []);
        }
      } catch (error) {
        console.error('Failed to fetch requests:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (user?.userRole === 'ADMIN') {
      fetchAllRequests();
    }
  }, [user, selectedStatus]);

  const updateRequestStatus = async (requestId: string, newStatus: string) => {
    try {
      // @ts-ignore
      await lumi.entities.ServiceRequests.update(requestId, { 
        status: newStatus,
        updatedAt: new Date().toISOString()
      });
      setRequests(prev => 
        prev.map(req => req._id === requestId ? { ...req, status: newStatus as any } : req)
      );
      toast.success(language === 'en' ? 'Status updated' : 'Statut mis à jour');
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error(language === 'en' ? 'Failed to update status' : 'Échec de la mise à jour du statut');
    }
  };

  const generateInvoice = async (request: ServiceRequest) => {
    setGeneratingInvoice(request._id);
    const loadingToast = toast.loading(language === 'en' ? 'Generating invoice...' : 'Génération de la facture...');

    try {
      // Calculate total based on services (you can customize this logic)
      const totalAmount = request.serviceTypes.length * 300; // Base estimate
      
      // Generate unique invoice number
      const invoiceNumber = `INV-${new Date().getFullYear()}-${String(Date.now()).slice(-6)}`;

      // Create invoice
      // @ts-ignore
      const invoice = await lumi.entities.Invoices.create({
        requestId: request._id,
        userId: request.userId,
        invoiceNumber,
        serviceTypes: request.serviceTypes,
        totalAmount,
        status: 'pending',
        paymentStatus: 'unpaid',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });

      // Update request with invoice ID
      // @ts-ignore
      await lumi.entities.ServiceRequests.update(request._id, {
        invoiceId: invoice._id,
        status: 'in-progress',
        updatedAt: new Date().toISOString()
      });

      // Send email notification to client
      await lumi.tools.email.send({
        to: request.contactEmail,
        subject: language === 'en' 
          ? `Invoice ${invoiceNumber} - Iron Hawk Ghana`
          : `Facture ${invoiceNumber} - Iron Hawk Ghana`,
        html: `
          <h2>${language === 'en' ? 'Invoice Ready for Review' : 'Facture Prête pour Révision'}</h2>
          <p>${language === 'en' ? 'Hello' : 'Bonjour'} ${request.contactName},</p>
          <p>${language === 'en' 
            ? 'Your service request has been reviewed and approved. Please log in to your dashboard to review and pay your invoice.'
            : 'Votre demande de service a été examinée et approuvée. Veuillez vous connecter à votre tableau de bord pour examiner et payer votre facture.'}</p>
          <div style="background: #f1f5f9; padding: 20px; border-radius: 8px; margin: 20px 0;">
            <p><strong>${language === 'en' ? 'Invoice Number' : 'Numéro de Facture'}:</strong> ${invoiceNumber}</p>
            <p><strong>${language === 'en' ? 'Total Amount' : 'Montant Total'}:</strong> $${totalAmount}</p>
          </div>
          <a href="${window.location.origin}/dashboard" style="display: inline-block; background: #0284c7; color: white; padding: 12px 24px; text-decoration: none; border-radius: 8px; margin: 16px 0;">
            ${language === 'en' ? 'View Invoice' : 'Voir la Facture'}
          </a>
          <p style="margin-top: 20px; color: #64748b; font-size: 14px;">
            ${language === 'en' 
              ? 'You can pay using Credit/Debit Card (Stripe), PayPal, or Mobile Money (MTN, Vodafone, AirtelTigo).'
              : 'Vous pouvez payer par Carte de Crédit/Débit (Stripe), PayPal, ou Mobile Money (MTN, Vodafone, AirtelTigo).'}
          </p>
        `
      });

      setRequests(prev => 
        prev.map(req => req._id === request._id ? { ...req, invoiceId: invoice._id, status: 'in-progress' } : req)
      );

      toast.success(language === 'en' ? 'Invoice generated and sent!' : 'Facture générée et envoyée!', { id: loadingToast });
    } catch (error) {
      console.error('Failed to generate invoice:', error);
      toast.error(language === 'en' ? 'Failed to generate invoice' : 'Échec de génération de la facture', { id: loadingToast });
    } finally {
      setGeneratingInvoice(null);
    }
  };

  const stats = {
    total: requests.length,
    submitted: requests.filter(r => r.status === 'submitted').length,
    inProgress: requests.filter(r => r.status === 'in-progress').length,
    completed: requests.filter(r => r.status === 'completed').length
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'submitted': return 'bg-blue-100 text-blue-700';
      case 'draft': return 'bg-gray-100 text-gray-700';
      case 'in-progress': return 'bg-amber-100 text-amber-700';
      case 'completed': return 'bg-green-100 text-green-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusLabel = (status: string) => {
    const labels: Record<string, Record<string, string>> = {
      'draft': { en: 'Draft', fr: 'Brouillon' },
      'submitted': { en: 'Submitted', fr: 'Soumis' },
      'in-progress': { en: 'In Progress', fr: 'En Cours' },
      'completed': { en: 'Completed', fr: 'Terminé' }
    };
    return labels[status]?.[language] || status;
  };

  if (user?.userRole !== 'ADMIN') {
    return null;
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-20">
      <div className="bg-slate-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl font-bold">
            {language === 'en' ? 'Admin Dashboard' : 'Tableau de Bord Admin'}
          </h1>
          <p className="text-slate-400 mt-1">
            {language === 'en' ? 'Manage client requests and generate invoices' : 'Gérer les demandes clients et générer des factures'}
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-100">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-50 text-blue-600 rounded-lg">
                <Users className="h-6 w-6" />
              </div>
              <div>
                <p className="text-sm text-gray-500">{language === 'en' ? 'Total' : 'Total'}</p>
                <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-100">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-amber-50 text-amber-600 rounded-lg">
                <Clock className="h-6 w-6" />
              </div>
              <div>
                <p className="text-sm text-gray-500">{language === 'en' ? 'Submitted' : 'Soumis'}</p>
                <p className="text-2xl font-bold text-slate-900">{stats.submitted}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-100">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-orange-50 text-orange-600 rounded-lg">
                <Package className="h-6 w-6" />
              </div>
              <div>
                <p className="text-sm text-gray-500">{language === 'en' ? 'In Progress' : 'En Cours'}</p>
                <p className="text-2xl font-bold text-slate-900">{stats.inProgress}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-100">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-50 text-green-600 rounded-lg">
                <CheckCircle className="h-6 w-6" />
              </div>
              <div>
                <p className="text-sm text-gray-500">{language === 'en' ? 'Completed' : 'Terminé'}</p>
                <p className="text-2xl font-bold text-slate-900">{stats.completed}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 mb-6">
          <div className="flex overflow-x-auto">
            {['all', 'submitted', 'in-progress', 'completed'].map(status => (
              <button
                key={status}
                onClick={() => setSelectedStatus(status)}
                className={`px-6 py-4 font-medium whitespace-nowrap border-b-2 transition-colors ${
                  selectedStatus === status
                    ? 'border-sky-600 text-sky-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {status === 'all' 
                  ? (language === 'en' ? 'All Requests' : 'Toutes les Demandes')
                  : getStatusLabel(status)}
              </button>
            ))}
          </div>
        </div>

        {/* Requests Table */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-100">
                <tr>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-slate-900">
                    {language === 'en' ? 'Client' : 'Client'}
                  </th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-slate-900">
                    {language === 'en' ? 'Services' : 'Services'}
                  </th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-slate-900">
                    {language === 'en' ? 'Location' : 'Localisation'}
                  </th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-slate-900">
                    {language === 'en' ? 'Status' : 'Statut'}
                  </th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-slate-900">
                    {language === 'en' ? 'Date' : 'Date'}
                  </th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-slate-900">
                    {language === 'en' ? 'Actions' : 'Actions'}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {requests.map(request => (
                  <tr key={request._id} className="hover:bg-slate-50">
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-medium text-slate-900">{request.contactName}</p>
                        <p className="text-sm text-gray-500">{request.contactEmail}</p>
                        <p className="text-sm text-gray-500">{request.phone}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1">
                        {request.serviceTypes.slice(0, 2).map(service => (
                          <span key={service} className="text-xs bg-sky-50 text-sky-700 px-2 py-1 rounded">
                            {service}
                          </span>
                        ))}
                        {request.serviceTypes.length > 2 && (
                          <span className="text-xs text-gray-500">+{request.serviceTypes.length - 2}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm text-gray-600">{request.city}, {request.country}</p>
                    </td>
                    <td className="px-6 py-4">
                      <select
                        value={request.status}
                        onChange={(e) => updateRequestStatus(request._id, e.target.value)}
                        className={`text-xs px-3 py-1.5 rounded-full font-medium border-0 ${getStatusColor(request.status)}`}
                      >
                        <option value="draft">{getStatusLabel('draft')}</option>
                        <option value="submitted">{getStatusLabel('submitted')}</option>
                        <option value="in-progress">{getStatusLabel('in-progress')}</option>
                        <option value="completed">{getStatusLabel('completed')}</option>
                      </select>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm text-gray-600">
                        {new Date(request.createdAt).toLocaleDateString(language === 'en' ? 'en-US' : 'fr-FR')}
                      </p>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex gap-2">
                        {request.status === 'submitted' && !request.invoiceId && (
                          <button
                            onClick={() => generateInvoice(request)}
                            disabled={generatingInvoice === request._id}
                            className="flex items-center gap-1 px-3 py-1.5 bg-sky-600 text-white text-xs rounded-lg hover:bg-sky-500 transition-colors disabled:opacity-50"
                          >
                            <FileText className="h-3 w-3" />
                            {generatingInvoice === request._id 
                              ? (language === 'en' ? 'Generating...' : 'Génération...')
                              : (language === 'en' ? 'Generate Invoice' : 'Générer Facture')}
                          </button>
                        )}
                        {request.invoiceId && (
                          <span className="flex items-center gap-1 px-3 py-1.5 bg-green-50 text-green-700 text-xs rounded-lg">
                            <CheckCircle className="h-3 w-3" />
                            {language === 'en' ? 'Invoice Sent' : 'Facture Envoyée'}
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;